import { Info } from "lucide-react";
import moment from "moment/moment";
import { Avatar, Combobox, Badge, Button } from "@/components/ui";
import { extractInitials, urlWithFormat, useSocialCoachSession, useSocialNetworks } from "@/utils";
import { SocialNetworksSection } from "@/components";
import * as React from "react";
import { Editor, VideoProjectDetail, VideoProjectStatus } from "@/interfaces";
import { VideoScriptModal } from "../VideoScriptModal";
import { UsersService, VideoProjectServices } from "@/services";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Svg } from "@/assets";

interface ProjectInfoCardProps {
  videoProject: VideoProjectDetail;
  setVideoProject: React.Dispatch<React.SetStateAction<VideoProjectDetail | undefined>>;
}

interface AssigneeOption {
  key: string | number;
  text: string;
  value: string | number;
  avatarUrl?: string;
}

const statusStyles: Record<VideoProjectStatus, { label: string; className: string }> = {
  [VideoProjectStatus.SUBMITTED_FOR_EDITING]: {
    label: "Submitted for Editing",
    className: "bg-blue-50 text-blue-700 border-blue-100",
  },
  [VideoProjectStatus.READY_TO_POST]: {
    label: "Ready to Post",
    className: "bg-amber-50 text-amber-700 border-amber-100",
  },
  [VideoProjectStatus.SCHEDULED]: {
    label: "Scheduled",
    className: "bg-purple-50 text-purple-700 border-purple-100",
  },
  [VideoProjectStatus.POSTED]: {
    label: "Posted",
    className: "bg-emerald-50 text-emerald-700 border-emerald-100",
  },
  [VideoProjectStatus.POST_FAILED]: {
    label: "Post Failed",
    className: "bg-rose-50 text-rose-700 border-rose-100",
  },
};

export function ProjectInfoCard({ videoProject, setVideoProject }: ProjectInfoCardProps) {
  const { userId } = useSocialCoachSession();
  const currentUserId = userId ? Number(userId) : undefined;

  const [openVideoScriptModal, setOpenVideoScriptModal] = React.useState<boolean>(false);
  const [savingAssignee, setSavingAssignee] = React.useState<boolean>(false);

  const includeUserIds = React.useMemo(() => {
    const ids = [];
    const assigneeUserId = videoProject.assigneeUserId;
    if (assigneeUserId) ids.push(assigneeUserId);
    if (currentUserId) ids.push(currentUserId);
    return [...new Set(ids)].sort((a, b) => a - b);
  }, [videoProject.assigneeUserId, currentUserId]);

  const { data: editorsData, isLoading: loadingEditors } = useQuery({
    queryKey: ["editors", includeUserIds],
    queryFn: () => UsersService.getEditors(includeUserIds),
    enabled: includeUserIds.length > 0,
    staleTime: 1000 * 60 * 30, // cache for 30 minutes
  });

  const editors = React.useMemo(() => {
    const rawContent = editorsData?.content || [];
    const assigneeUserId = videoProject.assigneeUserId;
    return [...rawContent].sort((a: Editor, b: Editor) => {
      if (a.userId === assigneeUserId) return -1;
      if (b.userId === assigneeUserId) return 1;
      if (currentUserId && a.userId === currentUserId) return -1;
      if (currentUserId && b.userId === currentUserId) return 1;
      if ((a.firstName || "") < (b.firstName || "")) return -1;
      if ((a.firstName || "") > (b.firstName || "")) return 1;
      return 0;
    });
  }, [editorsData, videoProject.assigneeUserId, currentUserId]);

  const postSnIds = React.useMemo(() => {
    return videoProject.post?.socialNetworks?.filter(sn => sn.status === "POSTED").map(sn => sn.socialNetworkId);
  }, [videoProject.post?.socialNetworks]);

  const postSnUrls = React.useMemo(() => {
    let snUrls = {};

    videoProject.post?.socialNetworks?.map(sn => {
      if (sn.externalPostUrl && sn.socialNetworkId !== "FB") {
        snUrls = {
          ...snUrls,
          [sn.socialNetworkId]: urlWithFormat(sn.externalPostUrl!.replace("[", "").replace("]", "")),
        };
      } else {
        snUrls = {
          ...snUrls,
          [sn.socialNetworkId]: sn.externalPostUrl!,
        };
      }
    });
    return snUrls;
  }, [videoProject.post?.socialNetworks]);

  const assigneeOptions = React.useMemo<AssigneeOption[]>(() => {
    const options: AssigneeOption[] = [
      {
        key: "unassigned",
        text: "Unassigned",
        value: "unassigned",
      },
    ];

    if (!loadingEditors && editors.length > 0) {
      editors.forEach(editor => {
        let text = [editor.firstName, editor.lastName].filter(Boolean).join(" ");
        if (text.length === 0) text = editor.username || editor.email || `User ${editor.userId}`;
        options.push({
          key: editor.userId,
          text: text,
          value: editor.userId,
          avatarUrl: editor.photoUrl || Svg.noImageuser,
        });
      });
    } else if (videoProject?.assigneeUserId) {
      options.push({
        key: videoProject.assigneeUserId,
        text: "Loading...",
        value: videoProject.assigneeUserId,
      });
    }

    return options;
  }, [editors, loadingEditors, videoProject?.assigneeUserId]);

  const selectedAssigneeValue = videoProject?.assigneeUserId ?? "unassigned";
  const selectedAssigneeItem = assigneeOptions.find(opt => {
    if (opt.value === "unassigned" && selectedAssigneeValue === "unassigned") return true;
    if (opt.value === "unassigned" || selectedAssigneeValue === "unassigned") return false;
    return Number(opt.value) === Number(selectedAssigneeValue);
  });

  const handleAssigneeChange = (selectedItem: AssigneeOption | undefined) => {
    if (!selectedItem) return;
    const value = selectedItem.value;
    let assigneeUserId = value === "unassigned" ? null : Number(value);

    setSavingAssignee(true);

    VideoProjectServices.setAssignee(videoProject!.videoProjectId!, assigneeUserId)
      .then(() => {
        setVideoProject(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            assigneeUserId,
          };
        });
        toast.success("Assignee updated successfully!");
      })
      .catch((e: any) => {
        console.log("Error updating assignee", e);
        toast.error("Failed to update assignee");
      })
      .finally(() => {
        setSavingAssignee(false);
      });
  };

  const { postableSocialNetworks } = useSocialNetworks();

  const statusInfo = statusStyles[videoProject.status] || {
    label: videoProject.statusLabel || videoProject.status,
    className: "bg-slate-50 text-slate-700 border-slate-100",
  };

  const isAssignedToMe =
    currentUserId &&
    videoProject.assigneeUserId !== null &&
    videoProject.assigneeUserId !== undefined &&
    Number(videoProject.assigneeUserId) === currentUserId;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row justify-between items-center w-full select-none">
        <label className="sectionHeader font-semibold text-slate-800 text-sm flex items-center gap-1.5">
          <Info className="size-4.5 text-blue-600" />
          Project Info
        </label>
      </div>
      <div className="videoProjectContainer topContainer flex flex-col gap-4 bg-slate-50/50 p-5 rounded-xl border border-slate-200">
        <div className="projectInfo flex flex-col gap-3.5 text-sm text-slate-600">
          <div className="flex flex-row justify-between items-center flex-wrap gap-2">
            <span className="font-medium text-slate-500">Status:</span>
            <div className="flex flex-row items-center gap-2">
              <Badge
                variant="outline"
                className={cn(
                  "font-semibold text-xs py-0.5 px-2 rounded-full border shadow-2xs select-none",
                  statusInfo.className
                )}
              >
                {statusInfo.label}
              </Badge>
              {videoProject.revisionCount !== undefined &&
                videoProject.revisionCount !== null &&
                videoProject.revisionCount > 0 && (
                  <Badge
                    variant="outline"
                    className={cn(
                      "font-semibold text-xs py-0.5 px-2 rounded-full border shadow-2xs select-none",
                      videoProject.revisionCount === 1
                        ? "bg-amber-100 text-amber-900 border-amber-300"
                        : videoProject.revisionCount === 2
                        ? "bg-orange-200 text-orange-900 border-orange-400"
                        : "bg-red-200 text-red-900 border-red-400"
                    )}
                  >
                    Revisions: {videoProject.revisionCount}
                  </Badge>
                )}
            </div>
          </div>
          <div className="flex flex-row justify-between items-center flex-wrap gap-2">
            <span className="font-medium text-slate-500">Status Date:</span>
            <span className="text-slate-400 text-xs font-medium">
              {moment(videoProject?.statusDate).format("MMM D, YYYY [at] h:mm a")}
            </span>
          </div>
          <div className="flex flex-row justify-between items-center flex-wrap gap-2">
            <span className="font-medium text-slate-500">Created:</span>
            <span className="text-slate-400 text-xs font-medium">
              {moment(videoProject?.createdDate).format("MMM D, YYYY [at] h:mm a")}
            </span>
          </div>
          <div className="flex flex-row justify-between items-start flex-wrap gap-2">
            <span className="font-medium text-slate-500 pt-1.5">Assignee:</span>
            <div className="flex flex-col items-end gap-1.5">
              <div className="flex items-center gap-2">
                {savingAssignee && (
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-indigo-600 border-t-transparent shrink-0" />
                )}
                <Combobox
                  className="max-w-[200px] w-full bg-white border border-slate-200"
                  placeholder="Select assignee"
                  disabled={savingAssignee}
                  items={assigneeOptions}
                  value={selectedAssigneeItem}
                  onPageSizeChange={undefined}
                  onValueChange={handleAssigneeChange}
                  itemToStringLabel={item => item.text}
                  itemToIcon={item => {
                    if (item.value === "unassigned") return null;
                    return (
                      <Avatar className="size-5 mr-1.5">
                        <Avatar.Image src={item.avatarUrl} />
                        <Avatar.Fallback className="text-[8px]">{extractInitials(item.text)}</Avatar.Fallback>
                      </Avatar>
                    );
                  }}
                />
              </div>
              {!isAssignedToMe && currentUserId && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold h-7 px-2"
                  disabled={savingAssignee}
                  onClick={() => {
                    const meItem = assigneeOptions.find(opt => Number(opt.value) === currentUserId);
                    if (meItem) {
                      handleAssigneeChange(meItem);
                    }
                  }}
                >
                  Assign to me
                </Button>
              )}
            </div>
          </div>
          {videoProject.videoScript && (
            <div className="flex flex-row justify-between items-center flex-wrap gap-2">
              <span className="font-medium text-slate-500">Script:</span>
              <span
                className="text-primary font-semibold hover:underline cursor-pointer"
                onClick={() => setOpenVideoScriptModal(true)}
              >
                {videoProject.videoScript.title}
              </span>
              <VideoScriptModal
                open={openVideoScriptModal}
                videoScript={videoProject.videoScript}
                onClose={() => setOpenVideoScriptModal(false)}
              />
            </div>
          )}
          {postSnIds && postSnIds.length > 0 && (
            <div className="pt-2 border-t border-slate-100">
              <SocialNetworksSection
                actives={postSnIds}
                links={postSnUrls}
                allSocialNetworks={postableSocialNetworks}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
